// apps/web/src/pages/office/Clients.jsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Search, FilterX, Home, PaintBucket, Edit, Calendar, Trash2,
  Eye, RefreshCw, AlertCircle, Users, User, Phone, Mail, MapPin, Clock, Upload, Copy,
  FolderOpen, Play, CheckCircle, Circle, XCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { format, subDays, subMonths, subYears, isWithinInterval } from 'date-fns';
import { getNextTempJobId, getNextConvertedJobId } from '../../lib/jobIdUtils';
import BulkImportLeads from '../../components/admin/BulkImportLeads';

// WhatsApp brand icon
const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Fixed formatDate to return a standard hyphen instead of special characters
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try { return format(new Date(dateString), 'dd MMM yyyy'); } catch { return 'Invalid date'; }
};

const STATUS_COLORS = {
  Lead: 'bg-slate-100 text-slate-800 border-slate-300',
  New: 'bg-blue-100 text-blue-800 border-blue-300',
  FollowUp: 'bg-amber-100 text-amber-900 border-amber-300',
  Connected: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  Confirm: 'bg-violet-100 text-violet-800 border-violet-300',
  Converted: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  Submitted: 'bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300',
  Lost: 'bg-rose-100 text-rose-800 border-rose-300',
};
const statusColorClass = (s) => STATUS_COLORS[s] || 'bg-gray-100 text-gray-800 border-gray-200';

const STATUS_TEXT_COLORS = {
  Lead: 'text-slate-700',
  New: 'text-blue-700',
  FollowUp: 'text-amber-700',
  Connected: 'text-cyan-700',
  Confirm: 'text-violet-700',
  Converted: 'text-emerald-700',
  Submitted: 'text-fuchsia-700',
  Lost: 'text-rose-700',
};

const DATE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7days', label: 'Last 7 Days' },
  { value: 'last30days', label: 'Last 30 Days' },
  { value: 'last6months', label: 'Last 6 Months' },
  { value: 'last1year', label: 'Last 1 Year' },
  { value: 'custom', label: 'Custom Range' },
];

const Clients = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [viewTab, setViewTab] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [updatingId, setUpdatingId] = useState(null); 
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [filterService, setFilterService] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [generatingJobIds, setGeneratingJobIds] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await refetch();
      toast.success('Refreshed');
    } catch (err) {
      toast.error('Refresh failed');
    } finally {
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  const userRole = currentUser?.role || 'sales';
  const isAdmin = ['admin', 'super_admin'].includes(userRole);
  const isManager = userRole === 'manager';
  const isSales = userRole === 'sales';
  const isDesigner = userRole === 'designer';
  const canViewAll = isAdmin || isManager || userRole === 'admin' || userRole === 'super_admin';

  const getLeadFilter = useMemo(() => {
    if (!currentUser || !currentUser.role) return '';
    if (canViewAll) return '';
    if (isSales) {
      return `(assignedSales = "${currentUser.id}" || assignedSales = null || assignedSales = "")`;
    }
    if (isDesigner) {
      return `status = "Converted" && assignedDesigner = "${currentUser.id}"`;
    }
    return '';
  }, [currentUser, canViewAll, isSales, isDesigner]);

  const {
    data: leads = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['leads', currentUser?.id, userRole],
    queryFn: async () => {
      if (!currentUser) return [];
      const filter = getLeadFilter;
      return await pb.collection('leads').getFullList({
        sort: '-created',
        filter: filter || undefined,
        expand: 'assignedSales,assignedDesigner',
      });
    },
    enabled: !!currentUser,
    retry: false,
  });

  const deleteLead = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await pb.collection('leads').delete(id);
      toast.success('Client deleted');
      refetch();
    } catch (err) {
      toast.error('Delete failed: ' + err.message);
    }
  };

  const generateMissingJobIds = async () => {
    await refetch();
    const currentLeads = queryClient.getQueryData(['leads', currentUser?.id, userRole]) || [];
    const missing = currentLeads.filter((lead) =>
      !lead.jobId ||
      lead.jobId === '' ||
      (lead.status === 'Converted' && !String(lead.jobId).startsWith('HD'))
    );
    if (missing.length === 0) {
      toast.info('All leads have valid Job IDs.');
      return;
    }
    setGeneratingJobIds(true);
    let success = 0;
    for (const lead of missing) {
      try {
        const newId =
          lead.status === 'Converted'
            ? await getNextConvertedJobId()
            : await getNextTempJobId();
        await pb.collection('leads').update(lead.id, { jobId: newId });
        success++;
      } catch (err) {
        console.error(err);
      }
    }
    setGeneratingJobIds(false);
    queryClient.invalidateQueries(['leads']);
    toast.success(`Generated ${success} Job IDs`);
    refetch();
  };

  const getDateRange = () => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (dateFilter) {
      case 'today':
        return { start: todayStart, end: now };
      case 'yesterday':
        return { start: subDays(todayStart, 1), end: subDays(todayStart, 1) };
      case 'last7days':
        return { start: subDays(now, 7), end: now };
      case 'last30days':
        return { start: subDays(now, 30), end: now };
      case 'last6months':
        return { start: subMonths(now, 6), end: now };
      case 'last1year':
        return { start: subYears(now, 1), end: now };
      case 'custom':
        if (customStartDate && customEndDate) {
          return { start: new Date(customStartDate), end: new Date(customEndDate) };
        }
        return null;
      default:
        return null;
    }
  };

  const isWithinDateRange = (createdDate) => {
    const range = getDateRange();
    if (!range) return true;
    const created = new Date(createdDate);
    return isWithinInterval(created, { start: range.start, end: range.end });
  };

  const filteredLeads = useMemo(() => {
    const list = leads.filter((lead) => {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        String(lead.name ?? '').toLowerCase().includes(searchLower) ||
        String(lead.phone ?? '').includes(search) ||
        String(lead.email ?? '').toLowerCase().includes(searchLower) ||
        String(lead.jobId ?? '').toLowerCase().includes(searchLower);

      const matchesService = filterService === 'All' || lead.serviceType === filterService;
      const matchesStatus = filterStatus === 'All' || lead.status === filterStatus;
      const matchesTab = (() => {
        if (viewTab === 'running') return lead.status === 'Converted';
        if (viewTab === 'submitted') return lead.status === 'Submitted';
        if (viewTab === 'confirm') return lead.status === 'Confirm';
        if (viewTab === 'lost') return lead.status === 'Lost';
        return !['Converted', 'Submitted', 'Lost'].includes(lead.status);
      })();
      const matchesDate = isWithinDateRange(lead.created);

      return matchesTab && matchesSearch && matchesService && matchesStatus && matchesDate;
    });

    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);
    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);
    const twoDaysAgo = new Date(now);
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
    const FOLLOWUP_STATUSES = ['FollowUp', 'Connected', 'Confirm'];
    const getFollowupDate = (lead) => {
      if (!lead.followupDate) return null;
      const d = new Date(lead.followupDate);
      return isNaN(d.getTime()) ? null : d;
    };

    const getPriority = (lead) => {
      const fd = getFollowupDate(lead);
      const isFollowupStatus = FOLLOWUP_STATUSES.includes(lead.status);

      if (isFollowupStatus && fd && fd >= startOfToday && fd <= endOfToday) return 1;
      if (isFollowupStatus && fd && fd < startOfToday && fd >= sevenDaysAgo) return 2;
      if (lead.status === 'New' && lead.created) {
        const created = new Date(lead.created);
        if (!isNaN(created.getTime()) && created >= twoDaysAgo) return 3;
      }
      if (lead.status === 'Converted') return 5;
      if (lead.status === 'Lost') return 6;
      if (lead.status === 'Submitted') return 7;
      return 4;
    };

    return [...list].sort((a, b) => {
      const pa = getPriority(a);
      const pb = getPriority(b);
      if (pa !== pb) return pa - pb;

      if (pa === 2) {
        const da = getFollowupDate(a);
        const db = getFollowupDate(b);
        if (da && db) return da.getTime() - db.getTime();
      }
      const ca = new Date(a.created || 0).getTime();
      const cb = new Date(b.created || 0).getTime();
      return cb - ca;
    });
  }, [leads, search, filterService, filterStatus, dateFilter, customStartDate, customEndDate, viewTab]);

  const isWhatsAppSentToday = (lead) => {
    if (!lead.notifiedAt) return false;
    try {
      const d = new Date(lead.notifiedAt);
      const t = new Date();
      return d.getFullYear() === t.getFullYear()
          && d.getMonth() === t.getMonth()
          && d.getDate() === t.getDate();
    } catch { return false; }
  };

  const markWhatsAppSent = (lead) => {
    if (isWhatsAppSentToday(lead)) return;
    pb.collection('leads')
      .update(lead.id, { notifiedAt: new Date().toISOString() })
      .then(() => queryClient.invalidateQueries())
      .catch((err) => console.warn('notifiedAt update failed:', err));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (filteredLeads.length > 0 && selectedIds.size === filteredLeads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredLeads.map((l) => l.id)));
    }
  };

  const changeStatusSingle = async (lead, newStatus) => {
    if (!newStatus || newStatus === lead.status) return;
    setUpdatingId(lead.id);
    try {
      const patch = { status: newStatus };
      if (newStatus === 'Converted' && (!lead.jobId || !String(lead.jobId).startsWith('HD'))) {
        try {
          patch.jobId = await getNextConvertedJobId();
        } catch (e) { console.warn('HD jobId generation failed:', e); }
      }
      await pb.collection('leads').update(lead.id, patch);
      toast.success('Status ? ' + newStatus + (patch.jobId ? ' � ' + patch.jobId : ''));
      queryClient.invalidateQueries();
    } catch (err) {
      toast.error('Update failed: ' + (err?.message || 'unknown'));
    } finally {
      setUpdatingId(null);
    }
  };

  const bulkChangeStatus = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkUpdating(true);
    const ids = [...selectedIds];
    let ok = 0, fail = 0;
    const converting = bulkStatus === 'Converted';
    for (const id of ids) {
      try {
        const lead = leads.find((l) => l.id === id);
        const patch = { status: bulkStatus };
        if (converting && lead && (!lead.jobId || !String(lead.jobId).startsWith('HD'))) {
          try { patch.jobId = await getNextConvertedJobId(); } catch (e) {}
        }
        await pb.collection('leads').update(id, patch);
        ok++;
      } catch { fail++; }
    }
    setBulkUpdating(false);
    toast.success('Updated ' + ok + (fail ? ' (' + fail + ' failed)' : ''));
    setSelectedIds(new Set());
    setBulkStatus('');
    queryClient.invalidateQueries();
  };

  const isOverdue = (followupDate) => {
    if (!followupDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fd = new Date(followupDate);
    fd.setHours(0, 0, 0, 0);
    return fd < today;
  };

  const handleCopyJobId = (jobId, e) => {
    e.stopPropagation();
    if (!jobId) return;
    
    const numericOnly = String(jobId).replace(/\D/g, '');
    
    if (!numericOnly) return;

    navigator.clipboard.writeText(numericOnly).then(() => {
      toast.success(`Copied ID: ${numericOnly}`);
    }).catch(err => {
      console.error('Failed to copy', err);
      toast.error('Failed to copy to clipboard');
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-red-200 p-8">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-semibold text-lg">Failed to load clients</p>
        <p className="text-sm text-slate-500 mb-4">{error.message}</p>
        <Button onClick={() => refetch()} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Gradient Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <Users className="w-8 h-8" /> Leads / Clients
            </h1>
            <p className="text-white/80">
              {canViewAll
                ? 'Full access'
                : isSales
                ? 'Your leads + unassigned'
                : 'Designer: converted leads only'}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <Button
                variant="outline"
                onClick={generateMissingJobIds}
                disabled={generatingJobIds}
                className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${generatingJobIds ? 'animate-spin' : ''}`}
                />
                {generatingJobIds ? 'Generating...' : 'Generate Job IDs'}
              </Button>
            )}
            {(isAdmin || isManager) && (
              <Button
                onClick={() => navigate('/office/clients/new')}
                className="bg-white text-indigo-600 hover:bg-indigo-50 hover:scale-105 active:scale-95 transition-all duration-200 shadow-lg"
              >
                <Plus className="w-5 h-5 mr-2" /> Onboard Client
              </Button>
            )}

            <Button
              variant="outline"
              onClick={() => setShowBulkImport(true)}
              className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white hover:scale-105 active:scale-95 transition-all"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Import
            </Button>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-white/10 backdrop-blur-sm text-white border-white/30 hover:bg-white/20 hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setViewTab('all')}
          className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 ${
            viewTab === 'all'
              ? 'bg-indigo-600 text-white shadow-lg'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <FolderOpen className="w-4 h-4" /> All Leads
        </button>
        <button
          onClick={() => setViewTab('running')}
          className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 ${
            viewTab === 'running'
              ? 'bg-emerald-600 text-white shadow-lg'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <Play className="w-4 h-4" /> Running Projects
        </button>
        <button
          onClick={() => setViewTab('submitted')}
          className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 ${
            viewTab === 'submitted'
              ? 'bg-violet-600 text-white shadow-lg'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <CheckCircle className="w-4 h-4" /> Submitted
        </button>
        <button
          onClick={() => setViewTab('confirm')}
          className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 ${
            viewTab === 'confirm'
              ? 'bg-purple-600 text-white shadow-lg'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <Circle className="w-4 h-4" /> Confirm
        </button>
        <button
          onClick={() => setViewTab('lost')}
          className={`px-4 py-2 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 ${
            viewTab === 'lost'
              ? 'bg-rose-600 text-white shadow-lg'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          <XCircle className="w-4 h-4" /> Lost
        </button>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
          <span className="font-semibold text-amber-800 text-sm">{selectedIds.size} selected</span>
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-sm"
          >
            <option value="">� Change status to �</option>
            {['Lead','FollowUp','Connected','Confirm','Converted','Submitted','Lost'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            onClick={bulkChangeStatus}
            disabled={!bulkStatus || bulkUpdating}
            className="px-4 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium text-sm disabled:opacity-50 transition"
          >
            {bulkUpdating ? 'Updating...' : 'Apply to all'}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="px-4 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-600 font-medium text-sm hover:bg-slate-50"
          >
            Clear
          </button>
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, phone, email or Job ID..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <Select value={filterService} onValueChange={setFilterService}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Services</SelectItem>
              <SelectItem value="building">
                <span className="flex items-center gap-1">
                  <Home className="w-4 h-4" /> Building
                </span>
              </SelectItem>
              <SelectItem value="interior">
                <span className="flex items-center gap-1">
                  <PaintBucket className="w-4 h-4" /> Interior
                </span>
              </SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Status</SelectItem>
              {['Lead', 'FollowUp', 'Connected', 'Confirm', 'Converted', 'Submitted', 'Lost'].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {dateFilter === 'custom' && (
            <div className="flex gap-2">
              <Input
                type="date"
                className="w-36"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
              <Input
                type="date"
                className="w-36"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
          )}

          {(search ||
            filterService !== 'All' ||
            filterStatus !== 'All' ||
            dateFilter !== 'all') && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setSearch('');
                setFilterService('All');
                setFilterStatus('All');
                setDateFilter('all');
                setCustomStartDate('');
                setCustomEndDate('');
              }}
            >
              <FilterX className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      {filteredLeads.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <p className="text-slate-500">No leads found.</p>
        </div>
      ) : (
        <Card className="border-0 shadow-xl overflow-hidden">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <tr>
                  <th className="px-3 py-2 w-10 text-center">
                    {isAdmin && filteredLeads.length > 0 && (
                      <input type="checkbox"
                        checked={filteredLeads.length > 0 && selectedIds.size === filteredLeads.length}
                        onChange={(e) => { e.stopPropagation(); toggleSelectAll(); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-slate-300 cursor-pointer"
                      />
                    )}
                  </th>
                  <th className="px-3 py-2 text-center font-semibold text-indigo-700 w-12">SL</th>
                  <th className="px-3 py-2 text-left font-semibold text-indigo-700">Job ID</th>
                  <th className="px-3 py-2 text-left font-semibold text-indigo-700">Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-indigo-700">Service</th>
                  <th className="px-3 py-2 text-left font-semibold text-indigo-700">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-indigo-700">Follow-up</th>
                  <th className="px-3 py-2 text-left font-semibold text-indigo-700">Sales</th>
                  <th className="px-3 py-2 text-left font-semibold text-indigo-700">Entry Date</th>
                  <th className="px-3 py-2 text-center font-semibold text-indigo-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLeads.map((lead, index) => (
                  <tr
                    key={lead.id}
                    onClick={(e) => { if (e.target.closest('select, input, button, a, svg, [role="button"]')) return; navigate(`/office/clients/${lead.id}`); }}
                    className="hover:bg-indigo-50/70 active:bg-indigo-100 cursor-pointer transition-all duration-150"
                  >
                    <td className="px-3 py-2 text-center">
                      <input type="checkbox"
                          checked={selectedIds.has(lead.id)}
                          onChange={(e) => { e.stopPropagation(); toggleSelect(lead.id); }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-slate-300 cursor-pointer"
                        />
                    </td>
                    <td className="px-3 py-2 text-center text-slate-500 font-medium text-sm">
                      {index + 1}
                    </td>
                    <td className="px-3 py-2 font-mono text-indigo-600 font-bold whitespace-nowrap">
                      {lead.jobId ? (
                        <button
                          onClick={(e) => handleCopyJobId(lead.jobId, e)}
                          title="Click to copy ID"
                          className="group flex items-center gap-1.5 hover:bg-indigo-100 px-2 py-0.5 rounded-md transition-colors"
                        >
                          <span>{lead.jobId}</span>
                          <Copy className="w-3 h-3 text-indigo-300 group-hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-all" />
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium">
                      <div className="relative inline-flex items-center gap-1.5">
                        {isWhatsAppSentToday(lead) && (
                          <span
                            title="WhatsApp sent today"
                            className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center text-white text-[10px] font-bold shadow-sm shrink-0"
                          >
                            ?
                          </span>
                        )}
                        <span className="text-indigo-600 hover:underline font-semibold">
                          {lead.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {lead.serviceType === 'building' ? (
                        <span className="flex items-center gap-1"><Home className="w-3.5 h-3.5" /> Building</span>
                      ) : (
                        <span className="flex items-center gap-1"><PaintBucket className="w-3.5 h-3.5" /> Interior</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={lead.status || 'Lead'}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        onChange={(e) => changeStatusSingle(lead, e.target.value)}
                        disabled={updatingId === lead.id}
                        className={"px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer outline-none disabled:opacity-50 " + statusColorClass(lead.status)}
                      >
                        {['Lead','FollowUp','Connected','Confirm','Converted','Submitted','Lost'].map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {lead.status === 'Converted' ? (
                        <span className="text-emerald-600 font-semibold">
                          {formatDate(lead.updated || lead.created)}
                          <span className="ml-1 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                            Converted
                          </span>
                        </span>
                      ) : lead.status === 'Submitted' ? (
                        <span className="text-fuchsia-600 font-semibold">
                          {formatDate(lead.updated || lead.created)}
                          <span className="ml-1 text-xs bg-fuchsia-100 text-fuchsia-700 px-1.5 py-0.5 rounded-full">
                            Submitted
                          </span>
                        </span>
                      ) : lead.status === 'Lost' ? (
                        <span className="text-slate-500">
                          {formatDate(lead.updated || lead.created)}
                          <span className="ml-1 text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded-full">
                            Lost
                          </span>
                        </span>
                      ) : (
                        <span
                          className={
                            isOverdue(lead.followupDate)
                              ? 'text-red-600 font-semibold'
                              : STATUS_TEXT_COLORS[lead.status] || 'text-slate-700'
                          }
                        >
                          {formatDate(lead.followupDate)}
                          {isOverdue(lead.followupDate) && (
                            <span className="ml-1 text-xs bg-red-100 text-red-700 px-1 py-0.5 rounded-full">
                              Overdue
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {/* Fixed: Shows a simple dash instead of a broken character if unassigned */}
                      {lead.expand?.assignedSales?.name || lead.assignedSales || '-'}
                    </td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{formatDate(lead.created)}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex gap-1 justify-center">
                        {lead.phone && (
                          <a
                            href={`https://wa.me/${String(lead.phone).replace(/\D/g, '')}?text=${encodeURIComponent(`Hi ${lead.name || 'there'}, this is HouseDesigns99. I would like to discuss your design enquiry with you.

Check out our latest designs: https://housedesigns99.com/portfolio

Reply for a free consultation.`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => { e.stopPropagation(); markWhatsAppSent(lead); }}
                            title="WhatsApp Marketing Message"
                            className="text-green-500 hover:text-green-700 p-1.5 rounded-lg hover:bg-green-100 active:scale-90 transition-all cursor-pointer"
                          >
                            <WhatsAppIcon className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/office/clients/${lead.id}`); }}
                          title="View"
                          className="text-indigo-500 hover:text-indigo-700 p-1.5 rounded-lg hover:bg-indigo-100 active:scale-90 transition-all duration-150 cursor-pointer"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {(canViewAll ||
                          (isSales && lead.assignedSales === currentUser?.id)) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/office/clients/${lead.id}?edit=true`); }}
                            title="Edit"
                            className="text-amber-500 hover:text-amber-700 p-1.5 rounded-lg hover:bg-amber-100 active:scale-90 transition-all duration-150 cursor-pointer"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteLead(lead.id, lead.name); }}
                            title="Delete"
                            className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-red-100 active:scale-90 transition-all duration-150 cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
      <BulkImportLeads
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImported={() => window.location.reload()}
      />
    </div>
  );
};

export default Clients;